import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Letter } from '../types/game';
import { findAllWords, getWordsByLength, WordPath } from '../utils/wordFinder';

interface DebugPanelProps {
  grid: Letter[][];
  showDebug: boolean;
  onToggleDebug: () => void;
  showGridDebug: boolean;
  onToggleGridDebug: () => void;
  onGridSizeChange?: (gridSize: number) => void;
}

const DebugPanel: React.FC<DebugPanelProps> = ({ grid, showDebug, onToggleDebug, showGridDebug, onToggleGridDebug, onGridSizeChange }) => {
  const [selectedLength, setSelectedLength] = useState<number | null>(null);
  const gridSizes = [3, 4, 5, 6]; // Max 6x6
  const currentGridSize = grid.length;
  
  const allWords = useMemo(() => {
    if (!showDebug) return [];
    return findAllWords(grid);
  }, [grid, showDebug]);
  
  const wordsByLength = useMemo(() => {
    return getWordsByLength(allWords);
  }, [allWords]);
  
  const totalWords = allWords.length;
  const lengths = Object.keys(wordsByLength).map(Number).sort((a, b) => b - a);
  
  const getWordsToShow = (): WordPath[] => {
    if (selectedLength !== null) {
      return wordsByLength[selectedLength] || [];
    }
    return allWords.slice(0, 50); // Show first 50 words to avoid overwhelming
  };
  
  return (
    <div className="debug-panel">
      {/* Header buttons hidden; toggled via floating FAB in App */}
      
      <AnimatePresence>
        {showDebug && (
          <motion.div
            className="debug-content"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="debug-header">
              <h3>Word List ({totalWords})</h3>
              
              {/* Grid Size Selector - Debug Only */}
              {onGridSizeChange && (
                <div style={{ marginBottom: '15px', paddingBottom: '15px', borderBottom: '1px solid #e9ecef' }}>
                  <div style={{ marginBottom: '8px', fontSize: '0.9rem', fontWeight: 600, color: '#333' }}>
                    Grid Size (Current: {currentGridSize}×{currentGridSize})
                  </div>
                  <div className="length-filters" style={{ justifyContent: 'flex-start' }}>
                    {gridSizes.map(size => (
                      <button
                        key={size}
                        className={`length-filter ${currentGridSize === size ? 'active' : ''}`}
                        onClick={() => onGridSizeChange(size)}
                        style={{ 
                          background: currentGridSize === size ? '#4CAF50' : '#e9ecef',
                          color: currentGridSize === size ? 'white' : '#333'
                        }}
                      >
                        {size}×{size}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="length-filters">
                <button
                  className={`length-filter ${selectedLength === null ? 'active' : ''}`}
                  onClick={() => setSelectedLength(null)}
                >
                  All
                </button>
                {lengths.map(length => (
                  <button
                    key={length}
                    className={`length-filter ${selectedLength === length ? 'active' : ''}`}
                    onClick={() => setSelectedLength(length)}
                  >
                    {length} letters ({wordsByLength[length].length})
                  </button>
                ))}
              </div>
            </div>
            
            <div className="words-list">
              {getWordsToShow().map((wordPath, index) => (
                <motion.div
                  key={`${wordPath.word}-${index}`}
                  className="debug-word"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.02 }}
                >
                  <span className="word-text">{wordPath.word}</span>
                  <span className="word-length">({wordPath.word.length})</span>
                </motion.div>
              ))}
              
              {selectedLength === null && allWords.length > 50 && (
                <div className="more-words">
                  ... and {allWords.length - 50} more words
                </div>
              )}
            </div>
            
            <div className="debug-stats">
              <div className="stat">
                <span className="stat-label">Total Words:</span>
                <span className="stat-value">{totalWords}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Longest Word:</span>
                <span className="stat-value">
                  {allWords.length > 0 ? `${allWords[0].word} (${allWords[0].word.length})` : 'None'}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Average Length:</span>
                <span className="stat-value">
                  {allWords.length > 0 
                    ? (allWords.reduce((sum, w) => sum + w.word.length, 0) / allWords.length).toFixed(1)
                    : '0'
                  }
                </span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default DebugPanel;
